import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../shared/Crud';
import deliveryService from '../../services/deliveryService';
import { formatCurrencyBR } from '../../utils/masks';
import DeliveryStatusBadge from '../Deliveries/DeliveryStatusBadge';
import DeliveryStatusMultiSelect from '../Deliveries/DeliveryStatusMultiSelect';
import { StatusFilterGroup } from '../Deliveries/DeliveriesList.styles';
import { getSelectableDeliveryStatusOptions } from '../Deliveries/statusConfig';
import { SearchIcon } from '../Layout/icons';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    getDefaultDeliveryDateRange,
    validateDeliveryDateRange,
    MAX_DELIVERY_PAGE_SIZE,
} from '../../utils/deliveryDateRange';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: min(720px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ModalHeader = styled.div`
    flex-shrink: 0;
    padding: 24px 24px 0;
`;

const ModalBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0 24px;
    overflow: hidden;
`;

const ScrollableList = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    margin-top: 16px;
`;

const ModalFooter = styled.div`
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px 24px;
    border-top: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e5e7eb'};
    background: white;
`;

const Title = styled.h2`
    margin: 0 0 16px;
    font-size: 18px;
`;

const Hint = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    color: #555;
    line-height: 1.5;
`;

const FilterHint = styled.p`
    margin: 8px 0 0;
    font-size: 12px;
    color: ${({ $error }) => ($error ? '#b91c1c' : '#6b7280')};
`;

function getDefaultFilters() {
    return {
        id: '',
        status: [],
        ...getDefaultDeliveryDateRange(),
    };
}

const defaultFilters = getDefaultFilters();

const { Button, TableContainer, Table, FiltersContainer, FilterGroup } = CrudStyles;

/**
 * Modal para selecionar entregas elegíveis e incluir cobranças na fatura.
 */
export default function AddDeliveriesModal({ open, customerId, onClose, onConfirm, submitting }) {
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [localFilters, setLocalFilters] = useState(defaultFilters);
    const [filterError, setFilterError] = useState(null);
    const debouncedFilters = useDebouncedValue(open ? localFilters : defaultFilters, 750);

    const fetchDeliveries = useCallback(async (filters) => {
        if (!customerId) return;

        const validation = validateDeliveryDateRange(filters.startDate, filters.endDate);
        if (!validation.valid) {
            setDeliveries([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await deliveryService.listBillable({
                page: 0,
                size: MAX_DELIVERY_PAGE_SIZE,
                sort: 'id,desc',
                customerId,
                id: filters.id || undefined,
                status: filters.status,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
            });
            setDeliveries(result.content || []);
        } catch {
            setDeliveries([]);
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        if (!open) return;
        setSelectedIds(new Set());
        setLocalFilters(getDefaultFilters());
        setFilterError(null);
    }, [open]);

    useEffect(() => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
        } else {
            setFilterError(null);
        }
    }, [localFilters.startDate, localFilters.endDate]);

    useEffect(() => {
        if (!open || !customerId) return;
        fetchDeliveries(debouncedFilters);
    }, [open, customerId, debouncedFilters, fetchDeliveries]);

    const handleStatusFilterChange = (selectedStatuses) => {
        setFilterError(null);
        setLocalFilters((prev) => ({ ...prev, status: selectedStatuses }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterError(null);
        setLocalFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
            return;
        }
        setFilterError(null);
        fetchDeliveries(localFilters);
    };

    const toggleSelection = (deliveryId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(deliveryId)) {
                next.delete(deliveryId);
            } else {
                next.add(deliveryId);
            }
            return next;
        });
    };

    const handleConfirm = () => {
        onConfirm(Array.from(selectedIds));
    };

    if (!open) return null;

    return (
        <Overlay role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <Title>Incluir entregas na fatura</Title>
                    <Hint>
                        Selecione entregas elegíveis para cobrança (com cobrança marcada e ainda não vinculadas a outra
                        fatura). Use os filtros para refinar a busca.
                    </Hint>
                </ModalHeader>

                <ModalBody>
                    <FiltersContainer style={{ marginBottom: 0 }}>
                        <FilterGroup>
                            <label htmlFor="add-delivery-search-id">Entrega</label>
                            <input
                                id="add-delivery-search-id"
                                type="number"
                                min="1"
                                name="id"
                                value={localFilters.id}
                                onChange={handleFilterChange}
                                placeholder="Nº da entrega"
                            />
                        </FilterGroup>
                        <StatusFilterGroup>
                            <DeliveryStatusMultiSelect
                                id="add-delivery-filter-status"
                                label="Status"
                                value={Array.isArray(localFilters.status) ? localFilters.status : []}
                                options={getSelectableDeliveryStatusOptions()}
                                onChange={handleStatusFilterChange}
                            />
                        </StatusFilterGroup>
                        <FilterGroup>
                            <label htmlFor="add-delivery-filter-start">Criação de</label>
                            <input
                                id="add-delivery-filter-start"
                                type="date"
                                name="startDate"
                                value={localFilters.startDate}
                                onChange={handleFilterChange}
                            />
                        </FilterGroup>
                        <FilterGroup>
                            <label htmlFor="add-delivery-filter-end">Criação até</label>
                            <input
                                id="add-delivery-filter-end"
                                type="date"
                                name="endDate"
                                value={localFilters.endDate}
                                onChange={handleFilterChange}
                            />
                        </FilterGroup>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleSearch}
                            disabled={loading}
                            style={{ height: '38px' }}
                        >
                            <SearchIcon />
                            Pesquisar
                        </Button>
                    </FiltersContainer>
                    <FilterHint $error={Boolean(filterError)}>
                        {filterError
                            || 'Exibe apenas entregas cobráveis. Filtro por data de criação (intervalo máximo de 60 dias).'}
                    </FilterHint>

                    <ScrollableList>
                        <TableContainer>
                            <Table style={{ minWidth: '480px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }} />
                                        <th>Entrega</th>
                                        <th>Status</th>
                                        <th>Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>
                                                Carregando entregas...
                                            </td>
                                        </tr>
                                    ) : deliveries.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>
                                                Nenhuma entrega elegível encontrada
                                            </td>
                                        </tr>
                                    ) : (
                                        deliveries.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(item.id)}
                                                        onChange={() => toggleSelection(item.id)}
                                                    />
                                                </td>
                                                <td>#{item.id}</td>
                                                <td>
                                                    <DeliveryStatusBadge state={item.state || item.status} compact />
                                                </td>
                                                <td>{item.value != null ? formatCurrencyBR(item.value) : '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </TableContainer>
                    </ScrollableList>
                </ModalBody>

                <ModalFooter>
                    <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={submitting || selectedIds.size === 0}
                    >
                        {submitting ? 'Incluindo...' : `Incluir (${selectedIds.size})`}
                    </Button>
                </ModalFooter>
            </ModalBox>
        </Overlay>
    );
}
